import { create } from "zustand";
import { toast } from "react-toastify";
import { deleteCookie } from "cookies-next";
import {
    deleteCardApi,
    paymentCardApi,
    paymentConfirmApi,
    updateCardApi,
    verifyCardApi,
} from "../../app/api/payment.js";
import {
    USER_COUNTRY,
    USER_CURRENCY,
    DUBAI_ISO,
    STYLE_MY_WARDROBE,
    SEVEN_STEP_SURVEY,
} from "../../public/Constants/EnumConstants";
import { resetSurvey } from "../../components/utils/utils";
import useUserStore from "./useUserStore";
import useCountryStore from "./useCountryStore";
import { getTransactions } from "../../lib/User/action";

const usePaymentStore = create((set, get) => ({
    userTransactions: null,
    transactionLoading: false,
    paymentError: null,
    paymentLoading: false,
    transactionId: null,
    saveCard: false,
    hasConfirmedSurvey: false,

    resetPayment: () =>
        set({
            userTransactions: null,
            transactionLoading: false,
            paymentError: null,
            paymentLoading: false,
            transactionId: null,
            saveCard: false,
            hasConfirmedSurvey: false,
        }),

    paymentCard: async (payload, surveyType) => {
        set({ paymentLoading: true, paymentError: null });

        try {
            const userCountry = useCountryStore?.getState();
            const promoCode =
                JSON.parse(localStorage.getItem("promo_code") || "null")
                    ?.code ?? null;
            const discountCode = localStorage.getItem("referral_code") || null;

            const baseUrl = process.env.NEW_WEB_URL;
            let successUrl = `${baseUrl}checkout/payment-success`;

            if (surveyType === SEVEN_STEP_SURVEY) {
                successUrl = `${baseUrl}checkout/seven-steps-payment-success`;
            } else if (userCountry?.flag !== DUBAI_ISO) {
                successUrl = `${baseUrl}checkout/sa-payment-success`;
            }

            const failureUrl = `${baseUrl}checkout`;

            const newPayload = {
                ...payload,
                promo_code: promoCode,
                discount_code: discountCode,
                success_url: successUrl,
                failure_url: failureUrl,
            };

            const response = await paymentCardApi(newPayload);

            if (response?.redirect_link) {
                window.location.href = response.redirect_link;
                return;
            }
        } catch (error) {
            console.error("Payment card error:", error);
            set({ paymentError: error });
        } finally {
            set({ paymentLoading: false });
        }
    },

    verifyCard: async (payload) => {
        set({ paymentLoading: true, paymentError: null });
        try {
            const data = await verifyCardApi(payload);
            if (data?.redirect_link) {
                set({ paymentLoading: false });
                window.location.href = data.redirect_link;
            } else if (data?.errors) {
                toast(`Card Verification Failed: ${data.errors}`, {
                    type: "error",
                });
                set({ paymentError: data.errors });
            } else {
                toast(`Card Verification Failed`, { type: "error" });
            }
            set({ paymentLoading: true, paymentError: null });
        } catch (error) {
            console.error("Verify card error:", error);
            set({ paymentLoading: false, paymentError: error });
        } finally {
            set({ paymentLoading: false });
        }
    },

    getUserTransactions: async () => {
        set({ transactionLoading: true, paymentError: null });
        try {
            const response = await getTransactions();
            if (response) {
                set({
                    userTransactions: response,
                    transactionLoading: false,
                });
            } else {
                set({ transactionLoading: false });
            }
            return response;
        } catch (error) {
            set({ transactionLoading: false, paymentError: error });
        }
    },

    PaymentConfirmation: async (payload) => {
        const getCustomerData = useUserStore?.getState()?.getCustomerData;
        set({ paymentLoading: true, paymentError: null });
        try {
            const data = await paymentConfirmApi(payload);
            if (data?.content) {
                await getCustomerData();
                if (data.content.type !== "style_my_wardrobe") resetSurvey();
                localStorage.removeItem("promo_code");
                localStorage.removeItem("referral_code");
                localStorage.removeItem("StyleMyWardrobeAnswers");
                deleteCookie(STYLE_MY_WARDROBE);
                set({ hasConfirmedSurvey: true, paymentLoading: false });
                return data;
            }
        } catch (error) {
            console.error("Survey payment confirm error:", error);
            set({ paymentError: error });
        } finally {
            set({ paymentLoading: false });
        }
    },

    deleteCard: async (payload) => {
        const getCustomerData = useUserStore?.getState()?.getCustomerData;
        set({ paymentLoading: true, paymentError: null });
        try {
            const { data } = await deleteCardApi(payload);
            await getCustomerData();
            if (data?.message) {
                toast("Card Deleted Successfully", { type: "success" });
            } else if (
                data?.errors &&
                Array.isArray(data.errors) &&
                data.errors.length
            ) {
                const [error] = data.errors;
                if (
                    error ===
                    "Cannot delete only card while box is under process"
                ) {
                    set({ paymentError: error });
                } else {
                    toast(error, { type: "error" });
                    set({ paymentError: error });
                }
            } else {
                toast("Something Went Wrong, try again!", { type: "error" });
            }
        } catch (error) {
            console.error("Delete card error:", error);
            set({ paymentError: error });
        } finally {
            set({ paymentLoading: false });
        }
    },
}));

export default usePaymentStore;
